/**
 * WCAG relative-luminance / contrast-ratio helpers used to validate that
 * org/tenant branding colors stay legible before they're applied as CSS vars.
 * Only hex colors (#rgb / #rrggbb) are supported — anything else is treated
 * as unvalidated and callers should fall back to a safe default.
 */

const HEX_RE = /^#([a-f\d]{3}|[a-f\d]{6})$/i

export const isValidHexColor = (value?: string | null): value is string => Boolean(value && HEX_RE.test(value.trim()))

const hexToRgb = (hex: string): [number, number, number] | null => {
  const match = HEX_RE.exec(hex.trim())
  if (!match) return null
  let value = match[1]
  if (value.length === 3) value = value.split('').map((c) => c + c).join('')
  const num = parseInt(value, 16)
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255]
}

const channelLuminance = (channel: number) => {
  const v = channel / 255
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
}

const relativeLuminance = (hex: string): number | null => {
  const rgb = hexToRgb(hex)
  if (!rgb) return null
  const [r, g, b] = rgb
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b)
}

export const contrastRatio = (hexA: string, hexB: string): number | null => {
  const lumA = relativeLuminance(hexA)
  const lumB = relativeLuminance(hexB)
  if (lumA === null || lumB === null) return null
  const lighter = Math.max(lumA, lumB)
  const darker = Math.min(lumA, lumB)
  return (lighter + 0.05) / (darker + 0.05)
}

export const meetsContrast = (fg: string, bg: string, minRatio = 4.5): boolean => {
  if (!isValidHexColor(fg) || !isValidHexColor(bg)) return false
  const ratio = contrastRatio(fg, bg)
  return ratio !== null && ratio >= minRatio
}

/** Picks whichever of light/dark reads better against `bg`; used to derive safe button/badge text. */
export const pickContrastText = (bg: string, light = '#ffffff', dark = '#0f172a'): string => {
  if (!isValidHexColor(bg)) return light
  const ratioLight = contrastRatio(bg, light) ?? 0
  const ratioDark = contrastRatio(bg, dark) ?? 0
  return ratioLight >= ratioDark ? light : dark
}

/** Returns `candidate` only if it's a valid hex color with sufficient contrast against `bg`; otherwise `fallback`. */
export const safeTextColor = (candidate: string | undefined | null, bg: string, fallback: string, minRatio = 4.5): string => {
  if (candidate && meetsContrast(candidate, bg, minRatio)) return candidate
  return fallback
}
